import { useEffect, useState, useCallback } from "react";
import { Bell, BellSlash, CircleNotch, CheckCircle, WarningCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  pushSupported,
  permissionState,
  getExistingSubscription,
  enablePushNotifications,
  disablePushNotifications,
  sendTestNotification,
} from "../lib/pushNotifications";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATE = {
  UNSUPPORTED: "unsupported",
  DENIED: "denied",
  OFF: "off",
  ON: "on",
  LOADING: "loading",
};

export default function NotificationsCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canDigest = isAdmin && !user?.plan?.is_basic;
  const [state, setState] = useState(STATE.LOADING);
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!pushSupported()) {
      setState(STATE.UNSUPPORTED);
      return;
    }
    const perm = permissionState();
    if (perm === "denied") {
      setState(STATE.DENIED);
      return;
    }
    const sub = await getExistingSubscription();
    setState(sub ? STATE.ON : STATE.OFF);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePushNotifications();
      toast.success("Notifications enabled");
      setState(STATE.ON);
    } catch (e) {
      if (e?.code === "permission_denied") {
        setState(STATE.DENIED);
        toast.error("Permission denied. Enable notifications in browser settings.");
      } else {
        toast.error(e?.message || "Could not enable notifications");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePushNotifications();
      toast.success("Notifications disabled");
      setState(STATE.OFF);
    } catch (e) {
      toast.error(e?.message || "Could not disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      await sendTestNotification();
      toast.success("Test notification sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSendDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await api.post("/notifications/digest/send-now");
      const sent = r.data?.sent ?? 0;
      const total = r.data?.total ?? 0;
      toast.success(`Daily digest sent to ${sent}/${total} admin(s).`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send digest");
    } finally {
      setDigestBusy(false);
    }
  };

  const isOn = state === STATE.ON;
  const isOff = state === STATE.OFF;
  const isDenied = state === STATE.DENIED;
  const isUnsupported = state === STATE.UNSUPPORTED;

  return (
    <div className="bg-white border border-zinc-300" data-testid="notifications-card">
      <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
        <div className="flex items-center gap-2">
          {isOn ? (
            <Bell size={18} weight="fill" className="text-emerald-600" />
          ) : (
            <BellSlash size={18} weight="duotone" className="text-zinc-500" />
          )}
          <h2 className="font-display text-lg font-semibold">Push alerts</h2>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest">
          {isOn && (
            <>
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              <span className="text-emerald-700">Active</span>
            </>
          )}
          {isOff && <span className="text-zinc-500">Off</span>}
          {isDenied && (
            <>
              <WarningCircle size={14} weight="fill" className="text-red-600" />
              <span className="text-red-700">Blocked</span>
            </>
          )}
          {isUnsupported && <span className="text-zinc-500">Unsupported</span>}
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-zinc-600 leading-relaxed">
          {isOn
            ? "You'll get a push alert when a part crosses its low-stock threshold or hits zero."
            : "Enable to receive low-stock and out-of-stock alerts on this device — even when the app is closed."}
        </p>

        {isUnsupported && (
          <div className="bg-zinc-100 border border-zinc-300 p-3 text-xs text-zinc-600">
            This browser doesn't support push notifications. Install ezItemizer as a PWA or open
            in Chrome / Edge / Firefox on Android or desktop.
          </div>
        )}

        {isDenied && (
          <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-700">
            Notifications are blocked for this site. Click the lock icon in your address bar and
            allow notifications, then reload.
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {(isOff || state === STATE.LOADING) && !isUnsupported && !isDenied && (
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy || state === STATE.LOADING}
              data-testid="enable-notifications-button"
              className="bg-[#1A2332] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:bg-[#243044] disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <CircleNotch size={14} className="animate-spin" /> : <Bell size={14} weight="fill" />}
              Enable alerts
            </button>
          )}

          {isOn && (
            <>
              <button
                type="button"
                onClick={handleTest}
                disabled={busy}
                data-testid="test-notification-button"
                className="bg-[#FF9800] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:bg-[#E68900] disabled:opacity-50 flex items-center gap-2"
              >
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <Bell size={14} />}
                Send test
              </button>
              <button
                type="button"
                onClick={handleDisable}
                disabled={busy}
                data-testid="disable-notifications-button"
                className="bg-white border border-zinc-300 text-zinc-700 px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-50 disabled:opacity-50 flex items-center gap-2"
              >
                <BellSlash size={14} />
                Disable
              </button>
            </>
          )}

          {canDigest && (
            <button
              type="button"
              onClick={handleSendDigest}
              disabled={digestBusy}
              data-testid="send-digest-now-button"
              className="bg-[#1A2332] hover:bg-[#243044] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
              title="Email today's stock + movement summary to all admins right now"
            >
              {digestBusy ? <CircleNotch size={14} className="animate-spin" /> : <EnvelopeSimple size={14} weight="duotone" />}
              Send digest now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
